import * as React from 'react';
import { useSelector } from 'react-redux';

import { PaginationWrapper } from '../../components/Pagination/components/PaginationWrapper';
import { PaginationItem } from '../../components/Pagination/components/PaginationItem';
import { usePagination } from './utils';
import { getPagination, getPaginationFilters } from '../../selectors/pagination';
import { getCatalogPath } from '../../utils/getQueryString';
import { getTotalCountPages } from '../../utils/pagination';
import { DEFAULT_PAGINATION, PAGINATION_WIDTH } from '../../constants/pagination';
import { smoothScrollToPosition } from '../../utils/smoothScrollToPosition';
import { getCurrentTab } from '../../selectors/tabs';
import { ETabs } from '../../types/redux/tabs';
import { getNewPageAgencies, getNewPageRealtors } from '../../actions/pagination';
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch';

export interface IPaginationContainerProps {
  paginationWidth?: number;
  disabled?: boolean;
}

export const PaginationContainer: React.FC<IPaginationContainerProps> = ({
  paginationWidth = PAGINATION_WIDTH,
  disabled,
}) => {
  const dispatch = useAppDispatch();
  const currentTab = useSelector(getCurrentTab);
  const { page, limit, total } = useSelector(getPagination) || DEFAULT_PAGINATION;
  const filters = useSelector(getPaginationFilters);

  /**
   * page, limit, total - будут всегда, так как, если нет данных
   * присваиваем дефолтное значение этих параметров
   */
  const pagesTotal = getTotalCountPages(total, limit);

  const onSelect = React.useCallback(
    (pageSelected: number) => {
      if (currentTab === ETabs.Realtors) {
        dispatch(getNewPageRealtors(pageSelected));
      } else {
        dispatch(getNewPageAgencies(pageSelected));
      }
      smoothScrollToPosition();
    },
    [currentTab, dispatch],
  );

  const { getText, handleNext, handlePrev, pagesList } = usePagination({
    pagesTotal,
    page,
    paginationWidth,
    onSelect,
  });

  if (pagesTotal === 1) {
    return null;
  }

  // Ссылки совпадают с URL, который экшены пагинации ставят через pushState
  const getHref = (pageNumber: number) => getCatalogPath(currentTab, pageNumber, filters);

  return (
    <PaginationWrapper
      onNext={handleNext}
      onPrev={handlePrev}
      prevHref={handlePrev && getHref(page - 1)}
      nextHref={handleNext && getHref(page + 1)}
      disabled={disabled}
    >
      {pagesList.map((pageNumber, itemIndex) => {
        const text = getText(pageNumber, itemIndex);
        const isActive = pageNumber === page;
        const onClickHandler = isActive ? undefined : () => onSelect(pageNumber);

        return (
          <PaginationItem
            key={pageNumber}
            isActive={isActive}
            href={isActive ? undefined : getHref(pageNumber)}
            onClick={onClickHandler}
          >
            {text}
          </PaginationItem>
        );
      })}
    </PaginationWrapper>
  );
};

PaginationContainer.displayName = 'PaginationContainer';
